import { Spinner } from "@blueprintjs/core";
import * as React from "react";
import { useParams } from "react-router";

import { EPlatform, ERequestState, HealthTestRequestApi, HealthTestRequestDto, RequestDto } from "../../../../api";
import { PageContainer } from "../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalRequestResume, DrupalStepper } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { DrupalHealthTestForm } from "./DrupalHealthTestForm";

export interface IDrupalHealthTestRequestPageProps {}

export const DrupalHealthTestRequestPage: React.FunctionComponent<IDrupalHealthTestRequestPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const healthTestRequestId = React.useMemo(() => +id, [id]);

  const { t } = useTl();
  const api = useApiService(HealthTestRequestApi);

  const [currentStep, setCurrentStep] = React.useState(0);

  const { data, loading, saveItem, saving } = useCrudApi<HealthTestRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (apiId = healthTestRequestId) =>
          apiId > 0
            ? api.get(apiId)
            : new HealthTestRequestDto({
                dogId: null,
                healthPackIds: [],
                request: new RequestDto({
                  requestDate: new Date(),
                  platform: EPlatform.PORTAL,
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: (d: HealthTestRequestDto) =>
          api.save(
            HealthTestRequestDto.fromJS({
              ...d,
              request: RequestDto.fromJS({
                ...d.request,
                platform: EPlatform.PORTAL,
              }),
            })
          ),
        onSavedRoute: (d) =>
          d.request.status === ERequestState.BILLED
            ? `${EDrupalRoutes.healthTestRequest}/${d.healthTestRequestId}`
            : EDrupalRoutes.requestList,
        deleteApiFn: () => {},
      }),
      [api, healthTestRequestId]
    ),
    [healthTestRequestId]
  );

  return loading ? (
    <Spinner />
  ) : (
    <PageContainer>
      <DrupalFieldSet
        title={
          currentStep === 2
            ? `${t(ETLCodes.Payment)}: ${t(ETLCodes.HealthTestRequests)}`
            : `${t(ETLCodes.NewRequest)}: ${t(ETLCodes.HealthTestRequests)}`
        }
      >
        {data?.healthTestRequestId && data?.healthTestRequestId > 0 ? undefined : (
          <DrupalStepper stepNumber={currentStep} />
        )}
        <div>
          {!data?.healthTestRequestId ||
          data?.healthTestRequestId <= 0 ||
          data?.request?.status === ERequestState.REQUESTED ? (
            <DrupalHealthTestForm
              healthTestRequestId={healthTestRequestId}
              stepNumber={currentStep}
              initialdata={data}
              loading={saving}
              onStepChange={(newStep) => setCurrentStep(newStep)}
              save={(formikdata) => saveItem(formikdata)}
            />
          ) : saving ? (
            <Spinner />
          ) : (
            <DrupalRequestResume
              requestForm={
                <DrupalHealthTestForm
                  showStepper={data?.request?.status !== ERequestState.BILLED}
                  healthTestRequestId={healthTestRequestId}
                  stepNumber={currentStep}
                  initialdata={data}
                  loading={saving}
                  onStepChange={(newStep) => setCurrentStep(newStep)}
                  save={(formikdata) => saveItem(formikdata)}
                />
              }
              requestDto={data?.request}
              bill={null}
            />
          )}
        </div>
      </DrupalFieldSet>
    </PageContainer>
  );
};
